import { useCallback, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react'

import type { WizardStep } from '@klear/ui/wizard/wizard'

export type WizardStepDef = {
  id: string
  label: string
  section?: string
  visible?: boolean
  isComplete?: () => boolean
  beforeNext?: () => boolean | Promise<boolean>
  next?: () => string | null
  dependsOn?: string[]
  render: () => ReactNode
}

export type WizardStepComponentProps<Ctx> = { ctx: Ctx }

export type WizardStepModule<Ctx> = {
  id: string
  label: string
  section?: string
  visible?: (ctx: Ctx) => boolean
  isComplete?: (ctx: Ctx) => boolean
  beforeNext?: (ctx: Ctx) => boolean | Promise<boolean>
  next?: (ctx: Ctx) => string | null
  dependsOn?: string[]
  component: ComponentType<WizardStepComponentProps<Ctx>>
}

export function bindWizardSteps<Ctx>(modules: Array<WizardStepModule<Ctx>>, ctx: Ctx): WizardStepDef[] {
  return modules.map((stepModule) => {
    const { visible, isComplete, beforeNext, next, component: StepComponent } = stepModule
    return {
      id: stepModule.id,
      label: stepModule.label,
      section: stepModule.section,
      visible: visible != null ? visible(ctx) : undefined,
      isComplete: isComplete != null ? () => isComplete(ctx) : undefined,
      beforeNext: beforeNext != null ? () => beforeNext(ctx) : undefined,
      next: next != null ? () => next(ctx) : undefined,
      dependsOn: stepModule.dependsOn,
      render: () => <StepComponent ctx={ctx} />
    }
  })
}

export type WizardStepSelection = 'free' | 'gated' | 'locked'

export type UseWizardFlowOptions = {
  initialStepId?: string
  onExitFirstStep?: () => void
  stepSelection?: WizardStepSelection
}

export type WizardFlow = {
  steps: WizardStep[]
  activeStepId: string
  setActiveStepId: (stepId: string) => void
  content: ReactNode
  isFirstStep: boolean
  isLastStep: boolean
  canNext: boolean
  canPrev: boolean
  isAdvancing: boolean
  goNext: () => Promise<void>
  goPrev: () => void
  onStepSelect: ((stepId: string) => void) | undefined
  isStepDisabled: ((stepId: string) => boolean) | undefined
  stepSelection: WizardStepSelection
}

function resolveVisibleSteps(defs: WizardStepDef[]): WizardStepDef[] {
  return defs.filter((def) => def.visible !== false)
}

function resolveNextStepId(visible: WizardStepDef[], activeStepId: string): string | null {
  const idx = visible.findIndex((s) => s.id === activeStepId)
  if (idx < 0) return null
  const branchTarget = visible[idx]?.next?.() ?? null
  if (branchTarget != null && visible.some((s) => s.id === branchTarget)) {
    return branchTarget
  }
  if (idx >= visible.length - 1) return null
  return visible[idx + 1]?.id ?? null
}

function resolvePrevStepId(visible: WizardStepDef[], activeStepId: string): string | null {
  const idx = visible.findIndex((s) => s.id === activeStepId)
  if (idx <= 0) return null
  return visible[idx - 1]?.id ?? null
}

function isStepReachable(visible: WizardStepDef[], stepId: string): boolean {
  const targetIdx = visible.findIndex((s) => s.id === stepId)
  if (targetIdx < 0) return false
  const target = visible[targetIdx]
  if (target?.dependsOn != null) {
    return target.dependsOn.every((depId) => {
      const dep = visible.find((s) => s.id === depId)
      return isStepComplete(dep)
    })
  }
  if (targetIdx === 0) return true
  for (let i = 0; i < targetIdx; i++) {
    if (!isStepComplete(visible[i])) return false
  }
  return true
}

function isStepComplete(def: WizardStepDef | undefined): boolean {
  if (def == null) return true
  if (def.isComplete == null) return true
  return def.isComplete()
}

export function useWizardFlow(stepDefs: WizardStepDef[], options: UseWizardFlowOptions = {}): WizardFlow {
  const { initialStepId, onExitFirstStep, stepSelection = 'free' } = options

  const visible = useMemo(() => resolveVisibleSteps(stepDefs), [stepDefs])
  const branching = useMemo(() => stepDefs.some((s) => s.next != null), [stepDefs])

  const fallbackStepId = visible[0]?.id ?? ''
  const [activeStepId, setActiveStepIdState] = useState(() => initialStepId ?? fallbackStepId)

  if (visible.length > 0 && !visible.some((s) => s.id === activeStepId)) {
    setActiveStepIdState(fallbackStepId)
  }

  const historyRef = useRef<string[]>([])
  const activeIdRef = useRef(activeStepId)
  activeIdRef.current = activeStepId
  const visibleRef = useRef(visible)
  visibleRef.current = visible

  const [isAdvancing, setIsAdvancing] = useState(false)
  const advancingRef = useRef(false)
  const advanceGenRef = useRef(0)

  const navigateTo = useCallback((stepId: string) => {
    if (stepId !== activeIdRef.current) historyRef.current.push(activeIdRef.current)
    setActiveStepIdState(stepId)
  }, [])

  const setActiveStepId = useCallback(
    (stepId: string) => {
      navigateTo(stepId)
    },
    [navigateTo]
  )

  const activeDef = visible.find((s) => s.id === activeStepId)
  const isFirstStep = visible.length === 0 || visible[0]?.id === activeStepId
  const isLastStep = visible.length === 0 || visible[visible.length - 1]?.id === activeStepId
  const canNext = !isLastStep && !isAdvancing && isStepComplete(activeDef)
  const canPrev = (!isFirstStep || onExitFirstStep != null) && !isAdvancing

  const goNext = useCallback(async () => {
    if (advancingRef.current) return
    const fromStepId = activeIdRef.current
    const visibleNow = visibleRef.current
    const def = visibleNow.find((s) => s.id === fromStepId)
    if (visibleNow.length === 0 || visibleNow[visibleNow.length - 1]?.id === fromStepId) return
    if (!isStepComplete(def)) return

    advancingRef.current = true
    setIsAdvancing(true)
    const gen = ++advanceGenRef.current
    try {
      if (def?.beforeNext != null) {
        const ok = await def.beforeNext()
        if (!ok) return
      }
      if (gen !== advanceGenRef.current || activeIdRef.current !== fromStepId) return
      const nextId = resolveNextStepId(visibleRef.current, fromStepId)
      if (nextId != null) navigateTo(nextId)
    } catch {
    } finally {
      if (gen === advanceGenRef.current) {
        advancingRef.current = false
        setIsAdvancing(false)
      }
    }
  }, [navigateTo])

  const goPrev = useCallback(() => {
    if (advancingRef.current) return
    if (isFirstStep) {
      onExitFirstStep?.()
      return
    }
    if (branching) {
      const history = historyRef.current
      while (history.length > 0) {
        const candidate = history.pop()
        if (candidate != null && visible.some((s) => s.id === candidate)) {
          setActiveStepIdState(candidate)
          return
        }
      }
    }
    const prevId = resolvePrevStepId(visible, activeStepId)
    if (prevId != null) setActiveStepIdState(prevId)
  }, [isFirstStep, onExitFirstStep, branching, visible, activeStepId])

  const onStepSelectFree = useCallback(
    (stepId: string) => {
      if (advancingRef.current) return
      navigateTo(stepId)
    },
    [navigateTo]
  )

  const onStepSelectGated = useCallback(
    (stepId: string) => {
      if (advancingRef.current) return
      if (!isStepReachable(visibleRef.current, stepId)) return
      navigateTo(stepId)
    },
    [navigateTo]
  )

  const resolvedOnStepSelect = useMemo(() => {
    if (stepSelection === 'locked') return undefined
    return stepSelection === 'gated' ? onStepSelectGated : onStepSelectFree
  }, [stepSelection, onStepSelectGated, onStepSelectFree])

  const isStepDisabledGated = useCallback((stepId: string) => !isStepReachable(visibleRef.current, stepId), [])
  const isStepDisabledLocked = useCallback((stepId: string) => stepId !== activeIdRef.current, [])
  const isStepDisabled = useMemo(() => {
    if (stepSelection === 'locked') return isStepDisabledLocked
    if (stepSelection === 'free') return undefined
    return isStepDisabledGated
  }, [stepSelection, isStepDisabledGated, isStepDisabledLocked])

  const steps: WizardStep[] = useMemo(
    () =>
      visible.map((s) => ({
        id: s.id,
        label: s.label,
        section: s.section
      })),
    [visible]
  )

  const content = activeDef?.render() ?? null

  return {
    steps,
    activeStepId,
    setActiveStepId,
    content,
    isFirstStep,
    isLastStep,
    canNext,
    canPrev,
    isAdvancing,
    goNext,
    goPrev,
    onStepSelect: resolvedOnStepSelect,
    isStepDisabled,
    stepSelection
  }
}
